import {WIDTH, HEIGHT, PADDING} from 'components/effects/plane/constants.js';
import {reader} from "messaging/reader.js";
import {publishPlanarBounce} from 'messaging/messages.js';
import {doc} from './documentation.js';
import {centroid, diameter} from './geometry.js';
import {getSlicedParameters, sliceParams} from './params.js';
import {toRadians} from './utils.js';
import {lazy} from './lazy.js';
export const NAVIER = 'NAVIER';
export const EXPAND = 'EXPAND';
export const NONE = 'NONE';
export const CONTRACT = 'CONTRACT';
export const SWAP = 'ROTATE';
export const LIFE = 'LIFE';
export const BOUNCE_1 = 'BOUNCE';
export const BOUNCE_2 = 'BOUNCE_2';
export const ROTATE = 'ROTATE_ANGLE';
export const SPIRAL = 'SPIRAL';
export const SCRIPT = 'SCRIPT';

const {sqrt, pow} = Math;
const {keys, values} = Object;

export const density = (args) =>
    (x,y, coord) => {
        return (coord.density || 1) * args[5].getViscosity(x,y)
    };


// expand a group of parameters from the center of the parameters
// by a distance of d

// every operation requires parameters, alongw ith other args

// these functions take in a mapping from parameter id -> coordinates
// and returns a similar mapping that is transformed

doc(
    "swap",
    {},
    "Swaps the parameter values cyclicly");
export const swap = () => {
    let slicer = sliceParams();
    return (parameters, ...args) => {
        parameters = getSlicedParameters(slicer, parameters, args);
        let transformed = {};
        let ids = keys(parameters);
        let firstPts = parameters[ids[0]];
        let i=0;
        for (let id of ids) {
            let coord;
            if (i === ids.length - 1) {
                coord = firstPts;
            } else {
                coord= parameters[ids[i+1]];
            }
            transformed[id] = {
                x: coord.x,
                y: coord.y,
                density: coord.density
            };
            i++;
        }
        return transformed;
    };
};

export const _expand = (parameters, factor, getDensity) => {
    let cntr = centroid(
        values(parameters));
    
    // for each point now figure out the expansion
    let transformed = {};
    for (let id in parameters) {
        let {x,y} = parameters[id];
        let _density = getDensity ? getDensity(x,y, parameters[id]) : 10;
        let _x = x + (x - cntr.x) * factor * 10 / _density;
        let _y = y + (y - cntr.y) * factor * 10 / _density;
        transformed[id] = {
            x: _x,
            y: _y,
            density: _density
        };
    }
    return transformed;
};

doc(
    "expand",
    {
        minWidth: "Minimum width of the expansion",
        maxWidth: "Maximum width of the expansion"
    },
    "Expands the values to maxWidth (ratio of initial width 0-1) and then contracts to minWidth (ratio of initial width)"
);
export const expand = (minWidth, maxWidth) => {
    let slicer = sliceParams();
    let flip = true;
    let initialDiameter = undefined;
    return (parameters, ... args) => {
        let _diameter = diameter(parameters);
        if (!initialDiameter) {
            initialDiameter = _diameter;
        }
        let _parameters = getSlicedParameters(slicer, parameters, args);
        let _minWidth = lazy(minWidth, ...args) * initialDiameter;
        let _maxWidth = lazy(maxWidth, ...args) * initialDiameter;
        let speed = args[0];

        // let _factor = lazy(factor, ...args);
        if (flip && (_diameter >= _maxWidth || _diameter >= WIDTH)) {
            flip = false;
        } else if (!flip && _diameter <= _minWidth) {
            flip = true;
        }
        
        return _expand(_parameters, flip ? speed/5.0: -speed/5.0, density(args));
    };
};

doc(
    "life",
    {},
    "Expands/contracts and randomizes");
export const life  = (isRandom=true) => {
    let slicer = sliceParams();
    let flip = false;
    let counter = 0;
    const _reader = reader('planarReset');
    return (parameters, ... args) => {
        let _parameters = getSlicedParameters(slicer, parameters, args);
        let og = parameters;
        parameters = _parameters;
        let speed = args[0];
        let width = args[1];
        let effect = args[5];

        if (_reader().some(
            x => x === effect.id)) {
            flip = false;
            counter = 0;
        }

        if (++counter % width  === 0) {
            flip = !flip;
        }

        let transformed = {};
        if (isRandom) {
            for (let id in parameters) {
                let coin = Math.random();

                let {x,y} = parameters[id];
                if (coin < 0.2) {
                    // do nothing
                    continue;
                } else if (coin < 0.4) {
                    // move to right
                    x--;
                } else if (coin < 0.6) {
                    // move to  left
                    x++;
                } else if (coin < 0.8) {
                    // move up
                    y++;
                } else {
                    // move down
                    y--;
                }
                transformed[id] = {
                    x, y
                };
            }
        } else {
            transformed = { ... parameters};
        }
        let _speed = flip ? Math.random()*speed/5.0 : -Math.random()*speed/5.0;
        if (!isRandom) {
            _speed = flip ? speed/10.0 : -speed/10.0;
        }
        return _expand(
            transformed,
            _speed,
            density(args)
        );
    };
};

doc(
    "rotate",
    {
        degrees: "The degrees of rotation"
    },
    "Rotates the parameter values by the center, according to the degrees (0-90)"
);
export const rotate = (degrees) => {
    let slicer = sliceParams();
    return (parameters, speed=1, ...args) => {
        parameters = getSlicedParameters(slicer, parameters, [speed, ... args]);
        let _degrees = lazy(degrees, speed, ... args);
        let {x,y} = centroid(values(parameters));

        // translate to origin
        let translated = translate(
            parameters,
            -x,
            -y);

        let radians = toRadians(_degrees) * speed;
        let rotated = {};
        keys(translated).forEach(
            id => {
                let {x,y} = translated[id];
                let _density = density([speed, ...args])(x,y,translated[id]);
                let _radians = radians * 10 / _density;
                rotated[id] = {
                    x: x * Math.cos(_radians) - y * Math.sin(_radians),
                    y: y * Math.cos(_radians) + x * Math.sin(_radians),
                    density: _density
                };
            });

        // now translate it back to the centroid
        return translate(
            rotated,
            x,
            y);
    };
};

const translate = (pts, x, y) => {
    let translated = {};
    keys(pts).forEach(
        id => translated[id] =
            {
                ... pts[id],
                x: pts[id].x + x,
                y: pts[id].y + y,
            });
    return translated;
};

